import React, { useState } from 'react';
import { CxPortalLight, PronetxLogoLight } from '@@/icons';
import { Button, Flex, Input, Label, Link, useTheme, View, Icon, Alert } from '@aws-amplify/ui-react';
import { signIn, confirmSignIn, signOut, SignInOutput } from 'aws-amplify/auth';
import { useCompanyEmail } from '@@/contexts/CompanyProvider';
import { useLoginBannerAndAvailability } from './hooks/useLoginBannerAndAvailability';
import { LoginPageBanner } from '../components/LoginPageBanner';
import '@@/app/app.scss';
import './login-page.css';
import { useQueryClient } from '@tanstack/react-query';
import { GET_ROLE_PERMISSIONS_QUERY_KEY } from '@@/products/portal/contexts/AuthProvider';

export default function LoginPage() {
  const queryClient = useQueryClient();

  const { tokens } = useTheme();
  const { clearCompanyEmail, companyEmail: storedCompanyEmail } = useCompanyEmail();
  const { banner, hideLogin, loading: bannerLoading } = useLoginBannerAndAvailability();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
    newPassword: false,
    confirmPassword: false
  });
  const [formState, setFormState] = useState({
    username: storedCompanyEmail,
    password: ''
  });
  const [errors, setErrors] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  // 'signin', 'newPassword', 'verifyEmail', 'forgotPassword', 'resetPassword'
  const [authStep, setAuthStep] = useState('signin');
  const [newPasswordForm, setNewPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [_currentUser, setCurrentUser] = useState<SignInOutput | null>(null);

  // Forgot password states
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetPasswordForm, setResetPasswordForm] = useState({
    code: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Email validation helper
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation helper
  const isValidPassword = (password: string) => {
    // At least 6 characters with uppercase, lowercase, numbers, and symbols
    const minLength = password.length >= 6;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    return minLength && hasLowercase && hasUppercase && hasNumbers && hasSymbols;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setPasswordResetSuccess(false); // Clear success message when attempting to sign in
    // Validate form
    const newErrors = {
      username: !formState.username ? 'Username is required' : '',
      password: !formState.password ? 'Password is required' : ''
    };
    setErrors(newErrors);

    if (!newErrors.username && !newErrors.password) {
      try {
        await signOut();
        const user = await signIn({
          username: formState.username || '',
          password: formState.password
        });

        queryClient.invalidateQueries({ queryKey: [GET_ROLE_PERMISSIONS_QUERY_KEY] });

        setCurrentUser(user);
        setError(null);

        if (!user.isSignedIn) {
          switch (user.nextStep.signInStep) {
            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
              setAuthStep('newPassword');
              break;
            case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
              // Handle email verification needed
              setAuthStep('verifyEmail');
              break;
            default:
              console.log('Unhandled auth step:', user.nextStep.signInStep);
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('User is not confirmed')) {
          // User needs to verify email
          setAuthStep('verifyEmail');
          setError('Please verify your email address. A verification code has been sent to your email.');
        } else if (errorMessage.includes('User does not exist')) {
          setError('User does not exist');
        } else if (errorMessage.includes('Incorrect username or password')) {
          setError('Incorrect username or password');
        } else if (errorMessage.includes('Password attempts exceeded')) {
          setError('Password attempts exceeded');
        } else {
          setError('An error occurred during sign in. Please try again.');
        }
        console.error('Login error:', errorMessage);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPasswordForm.newPassword !== newPasswordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      const result = await confirmSignIn({
        challengeResponse: newPasswordForm.newPassword
      });

      if (result.isSignedIn) {
        // Successfully signed in
        setAuthStep('signin');
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        setAuthStep('verifyEmail');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const result = await confirmSignIn({
        challengeResponse: verificationCode
      });

      if (result.isSignedIn) {
        // Successfully verified and signed in
        setAuthStep('signin');
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(forgotPasswordEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      // Import resetPassword from aws-amplify/auth
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: forgotPasswordEmail });
      setAuthStep('resetPassword');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isValidPassword(resetPasswordForm.newPassword)) {
      setError('Password must be at least 6 characters with uppercase, lowercase, numbers, and symbols');
      return;
    }

    try {
      setIsLoading(true);
      // Import confirmResetPassword from aws-amplify/auth
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: forgotPasswordEmail,
        confirmationCode: resetPasswordForm.code,
        newPassword: resetPasswordForm.newPassword
      });
      setPasswordResetSuccess(true);
      setAuthStep('signin');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = () => {
    return (
      <Alert
        variation='error'
        hasIcon={true}
        dismissButtonLabel='Close'
        isDismissible
        onDismiss={() => setError(null)}
        marginTop={5}
      >
        {error}
      </Alert>
    );
  };

  // Render different views based on authStep
  const renderAuthStep = () => {
    switch (authStep) {
      case 'newPassword':
        return (
          <form onSubmit={handleNewPassword} aria-label='Update password form'>
            <Flex position={'relative'} alignItems={'center'} gap={5} className='change-password-header'>
              <Button type='button' onClick={() => setAuthStep('signin')} size='small' ariaLabel='Back to sign in'>
                <Icon pathData='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z' ariaLabel='Back' />
              </Button>
              <h3>Update Password</h3>
            </Flex>
            <Flex direction='column' gap={5} position={'relative'}>
              <Label htmlFor='newPassword'>New Password</Label>
              <Input
                id='newPassword'
                type={showPassword.newPassword ? 'text' : 'password'}
                value={newPasswordForm.newPassword}
                onChange={(e) => setNewPasswordForm({ ...newPasswordForm, newPassword: e.target.value })}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                autoComplete='new-password'
                ariaLabel='Enter new password'
              />
              <Button
                type='button'
                onClick={() => setShowPassword({ ...showPassword, newPassword: !showPassword.newPassword })}
                position='absolute'
                right={0}
                bottom='0'
                backgroundColor='transparent'
                padding={4}
                borderRadius={'0 9px 9px 0'}
                border={`1px solid #b3bbbb`}
                height={58}
                width={54}
                ariaLabel={showPassword.newPassword ? 'Hide new password' : 'Show new password'}
              >
                <Icon
                  aria-hidden='true'
                  pathData={
                    showPassword.newPassword
                      ? 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'
                      : 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'
                  }
                />
              </Button>
            </Flex>

            <Flex direction='column' gap={5} marginTop={16} position={'relative'}>
              <Label htmlFor='confirmPassword'>Confirm Password</Label>
              <Input
                id='confirmPassword'
                type={showPassword.confirmPassword ? 'text' : 'password'}
                value={newPasswordForm.confirmPassword}
                onChange={(e) => setNewPasswordForm({ ...newPasswordForm, confirmPassword: e.target.value })}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                autoComplete='new-password'
                ariaLabel='Confirm new password'
              />
              <Button
                type='button'
                onClick={() => setShowPassword({ ...showPassword, confirmPassword: !showPassword.confirmPassword })}
                position='absolute'
                right={0}
                bottom='0'
                backgroundColor='transparent'
                padding={4}
                borderRadius={'0 9px 9px 0'}
                border={`1px solid #b3bbbb`}
                height={58}
                width={54}
                ariaLabel={showPassword.confirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                <Icon
                  aria-hidden='true'
                  pathData={
                    showPassword.confirmPassword
                      ? 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'
                      : 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'
                  }
                />
              </Button>
            </Flex>
            {error && errorMessage()}
            <Flex justifyContent={'center'} marginTop={16}>
              <Button
                type='submit'
                isLoading={isLoading}
                className='signing-amplify-button'
                loadingText='Updating password...'
                size='large'
                color={'#1E1E1E'}
                variation='primary'
                ariaLabel='Submit new password'
              >
                Change Password
              </Button>
            </Flex>
          </form>
        );

      case 'verifyEmail':
        return (
          <form onSubmit={handleVerifyEmail} aria-label='Email verification form'>
            <div className='verify-email-container'>
              <h1>Verify Email</h1>
              <p>
                We have sent a verification code to your email address. Please enter the code below to verify your email
                address.
              </p>
            </div>
            <Flex direction='column' gap={5}>
              <Label htmlFor='verificationCode'>Enter Verification Code</Label>
              <Input
                id='verificationCode'
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                ariaLabel='Enter email verification code'
              />

              <Button type='submit' isLoading={isLoading} ariaLabel='Verify email'>
                Verify Email
              </Button>
            </Flex>
          </form>
        );

      case 'forgotPassword':
        return (
          <form onSubmit={handleForgotPassword} aria-label='Forgot password form'>
            <Flex position={'relative'} alignItems={'center'} gap={5} className='change-password-header'>
              <Button type='button' onClick={() => setAuthStep('signin')} size='small' ariaLabel='Back to sign in'>
                <Icon pathData='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z' ariaLabel='Back' />
              </Button>
              <h3>Reset Password</h3>
            </Flex>
            <Flex direction='column' gap={5}>
              <Label htmlFor='forgotPasswordEmail'>Email Address</Label>
              <Input
                id='forgotPasswordEmail'
                type='email'
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                autoComplete='email'
                ariaLabel='Enter email address'
              />
            </Flex>
            {error && errorMessage()}
            <Flex justifyContent={'center'} marginTop={16}>
              <Button
                type='submit'
                isLoading={isLoading}
                className='signing-amplify-button'
                loadingText='Sending reset code...'
                size='large'
                color={'#1E1E1E'}
                variation='primary'
                ariaLabel='Send reset code'
              >
                Send Reset Code
              </Button>
            </Flex>
          </form>
        );

      case 'resetPassword':
        return (
          <form onSubmit={handleResetPassword} aria-label='Reset password form'>
            <div className='change-password-header'>
              <Flex position={'relative'} alignItems={'center'} gap={5}>
                <Button
                  type='button'
                  onClick={() => setAuthStep('forgotPassword')}
                  size='small'
                  ariaLabel='Back to forgot password'
                >
                  <Icon pathData='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z' ariaLabel='Back' />
                </Button>
                <h3>Reset Password</h3>
              </Flex>
              <p>
                We've sent a verification code to your email address. Please enter the code and your new password below.
              </p>
            </div>

            <Flex direction='column' gap={5}>
              <Label htmlFor='resetCode'>Verification Code</Label>
              <Input
                id='resetCode'
                value={resetPasswordForm.code}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, code: e.target.value })}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                ariaLabel='Enter verification code'
              />
            </Flex>

            <Flex direction='column' gap={5} marginTop={16} position={'relative'}>
              <Label htmlFor='resetNewPassword'>New Password</Label>
              <Input
                id='resetNewPassword'
                type={showPassword.newPassword ? 'text' : 'password'}
                value={resetPasswordForm.newPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                autoComplete='new-password'
                ariaLabel='Enter new password'
              />
              <Button
                type='button'
                onClick={() => setShowPassword({ ...showPassword, newPassword: !showPassword.newPassword })}
                position='absolute'
                right={0}
                bottom='0'
                backgroundColor='transparent'
                padding={4}
                borderRadius={'0 9px 9px 0'}
                border={`1px solid #b3bbbb`}
                height={58}
                width={54}
                ariaLabel={showPassword.newPassword ? 'Hide new password' : 'Show new password'}
              >
                <Icon
                  aria-hidden='true'
                  pathData={
                    showPassword.newPassword
                      ? 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'
                      : 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'
                  }
                />
              </Button>
            </Flex>

            <Flex direction='column' gap={5} marginTop={16} position={'relative'}>
              <Label htmlFor='resetConfirmPassword'>Confirm Password</Label>
              <Input
                id='resetConfirmPassword'
                type={showPassword.confirmPassword ? 'text' : 'password'}
                value={resetPasswordForm.confirmPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                className='login-form-container-input'
                fontWeight={tokens.fontWeights.medium}
                fontSize={tokens.fontSizes.medium}
                padding='medium'
                borderRadius={9}
                required
                autoComplete='new-password'
                ariaLabel='Confirm new password'
              />
              <Button
                type='button'
                onClick={() => setShowPassword({ ...showPassword, confirmPassword: !showPassword.confirmPassword })}
                position='absolute'
                right={0}
                bottom='0'
                backgroundColor='transparent'
                padding={4}
                borderRadius={'0 9px 9px 0'}
                border={`1px solid #b3bbbb`}
                height={58}
                width={54}
                ariaLabel={showPassword.confirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                <Icon
                  aria-hidden='true'
                  pathData={
                    showPassword.confirmPassword
                      ? 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'
                      : 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'
                  }
                />
              </Button>
            </Flex>
            {error && errorMessage()}
            <Flex justifyContent={'center'} marginTop={16}>
              <Button
                type='submit'
                isLoading={isLoading}
                className='signing-amplify-button'
                loadingText='Resetting password...'
                size='large'
                color={'#1E1E1E'}
                variation='primary'
                ariaLabel='Reset password'
              >
                Reset Password
              </Button>
            </Flex>
          </form>
        );

      default:
        return (
          <form onSubmit={handleSubmit} aria-label='Sign in form'>
            {passwordResetSuccess && (
              <Alert
                variation='success'
                hasIcon={true}
                dismissButtonLabel='Close'
                isDismissible
                onDismiss={() => setPasswordResetSuccess(false)}
                marginBottom={16}
              >
                Password reset successful! You can now sign in with your new password.
              </Alert>
            )}
            <Flex direction='column' gap={5}>
              <Label htmlFor='username' fontWeight={tokens.fontWeights.medium} color={'#404D5C'}>
                Username
              </Label>
              <Flex position='relative'>
                <Input
                  id='username'
                  name='username'
                  value={formState.username || ''}
                  onChange={(e) => {
                    setFormState({ ...formState, username: e.target.value });
                    if (passwordResetSuccess) setPasswordResetSuccess(false);
                  }}
                  disabled={true}
                  className='login-form-container-input'
                  fontWeight={tokens.fontWeights.medium}
                  fontSize={tokens.fontSizes.medium}
                  padding='medium'
                  border={errors.username ? '1px solid #FF4B4B' : '1px solid #b3bbbb'}
                  borderRadius={9}
                  paddingRight={'58px'}
                  required
                  autoComplete='username'
                  ariaLabel='Enter username'
                  aria-invalid={!!errors.username}
                  aria-describedby={errors.username ? 'username-error' : undefined}
                />

                <Button size='small' variation='link' onClick={clearCompanyEmail} ariaLabel='Clear email'>
                  Clear
                </Button>
              </Flex>

              {errors.username && (
                <View color='red' fontSize={tokens.fontSizes.small} id='username-error'>
                  {errors.username}
                </View>
              )}
            </Flex>

            <Flex direction='column' gap={5} marginTop={16}>
              <Label htmlFor='password' fontWeight={tokens.fontWeights.medium} color={'#404D5C'}>
                Password
              </Label>
              <Flex position='relative'>
                <Input
                  id='password'
                  name='password'
                  type={showPassword.password ? 'text' : 'password'}
                  value={formState.password}
                  onChange={(e) => {
                    setFormState({ ...formState, password: e.target.value });
                    if (passwordResetSuccess) setPasswordResetSuccess(false);
                  }}
                  className='login-form-container-input'
                  fontWeight={tokens.fontWeights.medium}
                  fontSize={tokens.fontSizes.medium}
                  padding='medium'
                  border={errors.password ? '1px solid #FF4B4B' : '1px solid #b3bbbb'}
                  borderRadius={9}
                  required
                  paddingRight={'58px'}
                  autoComplete='current-password'
                  ariaLabel='Enter password'
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <Button
                  type='button'
                  onClick={() => setShowPassword({ ...showPassword, password: !showPassword.password })}
                  position='absolute'
                  right={0}
                  bottom='0'
                  backgroundColor='transparent'
                  padding={4}
                  borderRadius={'0 9px 9px 0'}
                  border={`1px solid #b3bbbb`}
                  height={58}
                  width={54}
                  ariaLabel={showPassword.password ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    aria-hidden='true'
                    pathData={
                      showPassword.password
                        ? 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'
                        : 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.20.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'
                    }
                  />
                </Button>
              </Flex>
              {errors.password && (
                <View color='red' fontSize={tokens.fontSizes.small} id='password-error'>
                  {errors.password}
                </View>
              )}
            </Flex>

            <Flex justifyContent={'end'} marginTop={5}>
              <Link
                onClick={() => setAuthStep('forgotPassword')}
                color={'#4285F4'}
                fontWeight={tokens.fontWeights.semibold}
                fontSize={13}
                ariaLabel='Forgot password? Click here to reset your password'
                style={{ cursor: 'pointer' }}
              >
                Trouble signing in?
              </Link>
            </Flex>

            {error && errorMessage()}

            <Flex justifyContent={'center'} marginTop={16}>
              <Button
                type='submit'
                loadingText='Signing in...'
                className='signing-amplify-button'
                size='large'
                color={'#1E1E1E'}
                isLoading={isLoading}
                variation='primary'
                ariaLabel='Sign in to your account'
              >
                Sign In
              </Button>
            </Flex>
          </form>
        );
    }
  };

  return (
    <div className='admin-login-page-container' role='main' aria-label='Login page'>
      <div
        className='admin-login-page-container-content'
        style={{
          backgroundImage: 'url(/images/login-page-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <PronetxLogoLight className='PronetxLogo-logo' aria-label='Pronetx Logo' />
        <div className='form-container' role='form' aria-label='Login form'>
          <div className='form-container-header' role='banner'>
            <CxPortalLight aria-label='Pronetx' className='text-white' />
          </div>
          {!bannerLoading && banner && <LoginPageBanner banner={banner} />}
          {!bannerLoading && hideLogin === true ? (
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px', textAlign: 'center', color: 'white' }}>
              Service temporarily unavailable. Please try again later.
            </div>
          ) : (
            <div className='form-container-content'>
              <div role='region' aria-label='Authentication form'>
                {renderAuthStep()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
